import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined'
import CloseCircleOutlined from '@ant-design/icons/CloseCircleOutlined'
import { Divider, List, Skeleton, Tooltip } from 'antd'
import {
  LIBRA_TICKET_GROUP_ENTRY_TYPE,
  LibraTicket,
  useLibraTickets,
} from 'features/libra/ticket/libraTicketsApi'
import { useTicketUpdate } from 'features/libra/ticket/TicketUpdateContext'
import { TicketProgressBar } from 'features/libra/ticketMenu/TicketProgressBar'
import {
  LibraWorflowStepComponentProps,
  LibraWorkflow,
  useLibraWorkflowSteps,
} from 'features/libra/workflow/workflowStepsApi'
import { useGeckoNotifications } from 'features/notifications'
import first from 'lodash/first'
import sortBy from 'lodash/sortBy'
import { FC, memo, useCallback, useEffect, useMemo } from 'react'
import styled from 'styled-components'

import { OpenTicketButton } from './ticketGate/OpenTicketButton'
import {
  TicketGateConfig,
  TicketGateConfigSchema,
} from './ticketGate/ticketGateApi'
import { TruncatedTitle } from './ticketGate/TruncatedTitle'

/**
 * Helper function to check if a ticket has passed a given step
 * Uses a pre-computed step index map for O(1) lookups instead of O(n) findIndex
 */
export function isTicketPastStep(
  ticket: LibraTicket,
  stepIdxMap: ReadonlyMap<string, number>,
  targetStateId?: string
): boolean {
  // If no target step, check if workflow is complete
  if (!targetStateId) {
    return ticket.workflowComplete
  }
  // Get target step index
  const targetStepIdx = stepIdxMap.get(targetStateId)
  // If target step not found, ticket hasn't passed
  if (targetStepIdx === undefined) {
    return false
  }
  // Get ticket's current step index
  const ticketStepIdx = stepIdxMap.get(ticket.stepId)
  if (ticketStepIdx === undefined) {
    return false
  }
  // Ticket has passed if its step index > target step index
  return ticketStepIdx > targetStepIdx
}

function useTargetTicketGroupId(ticketGroupIdProp: string): {
  groupId: string
  isLoading: boolean
} {
  const { fieldValues, isLoading } = useTicketUpdate()
  return {
    groupId: (fieldValues[ticketGroupIdProp] as string) ?? '',
    isLoading,
  }
}

function useTargetWorkflow(targetTickets: ReadonlyArray<LibraTicket>): {
  workflow: LibraWorkflow | undefined
  isLoading: boolean
} {
  const { errorNotification } = useGeckoNotifications()
  const uniqueWorkflowIds = useMemo<ReadonlySet<string>>(() => {
    const workflowIds = targetTickets.reduce((acc, ticket) => {
      acc.add(ticket.workflowId)
      return acc
    }, new Set<string>())
    return workflowIds
  }, [targetTickets])
  const { libraWorkflows, isLoading } = useLibraWorkflowSteps(
    useMemo(
      () => ({
        ids: [...uniqueWorkflowIds],
      }),
      [uniqueWorkflowIds]
    )
  )
  if (uniqueWorkflowIds.size > 1) {
    errorNotification('Multiple workflows found in target tickets')
    return { workflow: undefined, isLoading }
  }
  return { workflow: first(libraWorkflows), isLoading }
}

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
`

const TicketGateRowContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  padding: 0.5rem;
  width: 100%;
`

const TicketGateRowProcessBarContainer = styled.span`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  align-items: center;
  gap: 0.8rem;
  flex: 1;
`

const PassedIcon = styled(CheckCircleOutlined)`
  font-size: 1.5rem;
  color: green;
`

const FailedIcon = styled(CloseCircleOutlined)`
  font-size: 1.5rem;
  color: red;
`

export const Component: FC<
  LibraWorflowStepComponentProps<TicketGateConfig>
> = ({ config, setValidationResult }) => {
  const { targetStateId, targetTicketGroupIdProp } = useMemo(
    () => TicketGateConfigSchema.parse(config),
    [config]
  )
  const { groupId: ticketGroupId, isLoading: groupIdIsLoading } =
    useTargetTicketGroupId(targetTicketGroupIdProp)

  const { tickets, isLoading: subTicketsLoading } = useLibraTickets(
    useMemo(
      () => ({
        [LIBRA_TICKET_GROUP_ENTRY_TYPE]: ticketGroupId,
      }),
      [ticketGroupId]
    )
  )
  const { workflow, isLoading: targetWorkflowIsLoading } =
    useTargetWorkflow(tickets)
  const sortedTickets = useMemo(() => {
    return sortBy(tickets, 'title', 'id')
  }, [tickets])

  const renderTicketGateRow = useCallback(
    (ticket: LibraTicket) =>
      workflow ? (
        <TicketGateRow
          ticket={ticket}
          workflow={workflow}
          targetStateId={targetStateId}
          stepIdxMap={workflow.stepIndices}
        />
      ) : null,
    [workflow, targetStateId]
  )

  const isLoading =
    groupIdIsLoading || subTicketsLoading || targetWorkflowIsLoading

  const isStepComplete = useMemo(() => {
    if (isLoading) {
      return false
    }
    if (!workflow) {
      return false
    }
    return tickets.every((ticket) =>
      isTicketPastStep(ticket, workflow.stepIndices, targetStateId)
    )
  }, [isLoading, tickets, workflow, targetStateId])

  useEffect(() => {
    if (isLoading) {
      setValidationResult({
        status: 'Warning',
        message: 'Loading sub-ticket progress...',
      })
      return
    }
    if (!workflow) {
      setValidationResult({
        status: 'Error',
        message: 'Unable to determine sub-ticket progress',
      })
      return
    }
    setValidationResult(
      isStepComplete
        ? undefined
        : {
            status: 'Error',
            message:
              'Step incomplete - all sub-tickets must pass the required step',
          }
    )
  }, [isLoading, isStepComplete, setValidationResult, workflow])

  return workflow ? (
    <StepContainer>
      <List
        loading={isLoading}
        dataSource={sortedTickets}
        renderItem={renderTicketGateRow}
      />
    </StepContainer>
  ) : isLoading ? (
    <Skeleton active paragraph={{ rows: 10 }} />
  ) : null
}

const RawTicketGateRow: FC<{
  ticket: LibraTicket
  workflow: LibraWorkflow
  targetStateId?: string
  stepIdxMap: ReadonlyMap<string, number>
}> = ({ ticket, workflow, targetStateId, stepIdxMap }) => {
  const isTicketPassed = useMemo(
    () => isTicketPastStep(ticket, stepIdxMap, targetStateId),
    [ticket, stepIdxMap, targetStateId]
  )
  return (
    <List.Item>
      <TicketGateRowContainer>
        <TruncatedTitle title={ticket.title} />
        <TicketGateRowProcessBarContainer>
          <TicketProgressBar
            ticket={ticket}
            targetStateId={targetStateId}
            workflow={workflow}
          />
          {isTicketPassed ? <PassedIndicator /> : <FailedIndicator />}
          <Divider orientation='vertical' />
          <OpenTicketButton ticket={ticket} />
        </TicketGateRowProcessBarContainer>
      </TicketGateRowContainer>
    </List.Item>
  )
}
const TicketGateRow = memo(RawTicketGateRow)

const PassedIndicator = memo(() => (
  <Tooltip title='Sub-ticket passed required step'>
    <PassedIcon />
  </Tooltip>
))
PassedIndicator.displayName = 'PassedIndicator'

const FailedIndicator = memo(() => (
  <Tooltip title='Sub-ticket has not passed required step - please action ticket to proceed'>
    <FailedIcon />
  </Tooltip>
))
FailedIndicator.displayName = 'FailedIndicator'
